declare const window: any;

/**
 * Idempotent, promise-based loaders for the YouTube IFrame API and the Vimeo Player SDK.
 *
 * `src/index.html` already loads both as global `<script>` tags on every boot, so the common case
 * resolves immediately. This only does real work the day one of them is missing: a fresh request is
 * injected once (chaining any pre-existing `onYouTubeIframeAPIReady`, never overwriting it the way
 * `tema.page.ts`'s `loadYouTubeAPI()` does), and a watchdog re-injects once more if an adblocker or
 * ITP silently dropped the first attempt.
 */
const sdkPromises = new Map<string, Promise<void>>();
const REINJECT_AFTER_MS = 4000;

function loadSdk(key: string, testReady: () => boolean, inject: (onReady: () => void) => void): Promise<void> {
  if (testReady()) return Promise.resolve();

  const cached = sdkPromises.get(key);
  if (cached) return cached;

  const promise = new Promise<void>((resolve) => {
    const settle = () => {
      if (testReady()) resolve();
    };
    inject(settle);
    setTimeout(() => {
      if (!testReady()) inject(settle);
    }, REINJECT_AFTER_MS);
  });

  sdkPromises.set(key, promise);
  return promise;
}

export function loadYouTubeApi(): Promise<void> {
  return loadSdk(
    'youtube',
    () => !!window.YT?.Player,
    (onReady) => {
      const previous = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        previous?.();
        onReady();
      };
      if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
        const script = document.createElement('script');
        script.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(script);
      }
    }
  );
}

export function loadVimeoApi(): Promise<void> {
  return loadSdk(
    'vimeo',
    () => !!window.Vimeo?.Player,
    (onReady) => {
      let script = document.querySelector<HTMLScriptElement>('script[src*="player.vimeo.com/api/player.js"]');
      if (!script) {
        script = document.createElement('script');
        script.src = 'https://player.vimeo.com/api/player.js';
        document.head.appendChild(script);
      }
      script.addEventListener('load', onReady, { once: true });
    }
  );
}
